import { useState, useEffect } from 'react';
import { makeStyles } from 'design-system/product/theme';
import { JSONAnnotation } from '@scale/shared/types/response/textcollection';
import { JSONField } from '@scale/shared/types/params/textcollection';
import Editor from '@monaco-editor/react';
import clsx from 'clsx';

interface JSONFieldSectionProps {
  field: JSONField;
  annotation: JSONAnnotation;
  setAnnotation: (annotation: JSONAnnotation) => void;
}

export default function JSONFieldSection({
  field,
  annotation,
  setAnnotation,
}: JSONFieldSectionProps) {
  const classes = useStyles();
  const [editorHeight, setEditorHeight] = useState(100);
  const [rawJson, setRawJson] = useState<string>(
    JSON.stringify(annotation?.response?.[0], null, 2) || '',
  );
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true);
  useEffect(() => {
    // update the editor height when the rawJson changes
    const lines = rawJson.split('\n').length;
    const newHeight = Math.min(Math.max(lines * 18, 100), 400);
    setEditorHeight(newHeight);
  }, [rawJson]);

  const onEditRawJSON = (text: string | undefined) => {
    if (text === undefined) {
      return;
    }
    try {
      setRawJson(text);
      if (text !== '') {
        const value = JSON.parse(text);
        setIsJsonValid(true);
        setAnnotation({
          ...annotation,
          response: [value],
        });
      }
    } catch (error) {
      setIsJsonValid(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>{field.title}</div>
      <div className={classes.editor} style={{ height: `${editorHeight}px` }}>
        <Editor
          language="json"
          options={{
            automaticLayout: true,
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            formatOnPaste: true,
            formatOnType: true,
            scrollBeyondLastLine: false,
            tabSize: 2,
            minimap: { enabled: false },
            lineNumbers: 'on',
            wordWrap: 'on',
          }}
          value={rawJson}
          onChange={onEditRawJSON}
        />
        <div
          className={clsx(
            classes.savedTag,
            isJsonValid ? classes.savedTagSaved : classes.savedTagUnsaved,
          )}
        >
          {isJsonValid ? 'Saved' : 'Not saved'}
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
  },
  editor: {
    position: 'relative',
    transition: 'height 0.2s ease-out',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.grey[300]}`,
    '&:hover $savedTag': {
      opacity: 1,
    },
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  savedTag: {
    position: 'absolute',
    right: 4,
    bottom: 4,
    outline: 'none',
    border: 'none',
    padding: '4px 8px',
    fontSize: 12,
    borderRadius: 8,
    opacity: 0,
    transition: 'all 150ms ease-out',
  },
  savedTagSaved: {
    background: '#f6ffed',
    color: '#52c41a',
  },
  savedTagUnsaved: {
    background: '#fff1f0',
    color: '#ff4d4f',
  },
}));
